/**
 * ConfirmPage styles — CWR 2.7.
 *
 * Ports the confirmation + review mockups (confirmation-page.html /
 * review-page.html) onto the INJECTED per-tenant client tokens (never the
 * mockups' literal :root hex). Structure mirrors the 2.5 form page / 2.6 payment
 * page: a fixed page, an inner scroller, a glass Back (REVIEW only), a pinned
 * BookingHero, a rising sheet with the status header + detail rows, and a
 * floating footer CTA bar with a gradient scrim (the 2.6 rail). The page has no
 * text inputs, so the footer's keyboard-delta offset resolves to 0 — the formula
 * is kept for parity with the sibling pages. The dark scrim + neutral grab handle
 * are image-space (sanctioned literals, same as 2.4/2.5/2.6).
 */

/* ── Page + scroll (fullscreen adaptation of the mockup .iphone-frame / .app) ── */
.page {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: var(--app-viewport-stable-height, 100dvh);
  max-width: 480px;
  margin: 0 auto;
  background: var(--bg-page);
  color: var(--text-primary);
  overflow: hidden;
  font-family: var(--font-body);
}
.scroll {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: var(--app-viewport-height, 100dvh);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  z-index: 2;
  /* Clear the floating footer bar (up to 3 stacked buttons on PENDING) + safe-area
     so the last sheet content is never hidden under it. */
  padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 190px);
  overscroll-behavior: none;
  overflow-anchor: none;
}
.scroll::-webkit-scrollbar {
  display: none;
}
/* Transparent window in the scroller revealing the pinned hero underneath (= hero height). */
.heroSpacer {
  height: 330px;
  flex-shrink: 0;
  pointer-events: none;
}

/* ── Back button — glass icon square, pixel-identical to the 2.4/2.5/2.6 pages
     (smoke ruling 2026-07-02: overrides the review mockup's .tb-back text pill). ── */
/* rd-bookings — positioning only; the glass square + arrow are owned by the
   shared FlowBackButton (design D6). */
.back {
  position: fixed;
  top: calc(
    var(--tg-safe-area-inset-top, env(safe-area-inset-top, 0px)) + var(--tg-content-safe-area-inset-top, 0px) + 12px
  );
  left: 18px;
  z-index: 80;
}

/* ── Rising sheet — scrolls UP over the pinned hero (matches the 2.4/2.5/2.6 pages) ── */
.sheet {
  position: relative;
  z-index: 1;
  border-radius: 48px 48px 0 0;
  background: var(--bg-page);
  /* Upward only: the negative spread keeps the blur from spilling past the flat
     bottom edge, which drew a dark band under a short sheet on non-black looks. */
  box-shadow: 0 -10px 16px -6px rgba(0, 0, 0, 0.24);
  min-height: 60vh;
}
.grab {
  width: 38px;
  height: 5px;
  border-radius: 3px;
  background: rgba(var(--fg-page-rgb), 0.22);
  margin: -4px auto 0;
}
.body {
  padding: 18px 24px 0;
  display: flex;
  flex-direction: column;
}

/* ── Status header — CONFIRMED / PENDING (icon circle + title + subtitle) ── */
.statusHead {
  display: flex;
  align-items: center;
  gap: 13px;
  padding-bottom: 18px;
  margin-bottom: 4px;
  border-bottom: 1px solid var(--border-subtle);
}
.statusIcon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  color: var(--text-primary);
  background: radial-gradient(
    circle at 50% 40%,
    rgba(var(--accent-hot-rgb), 0.42),
    rgba(var(--accent-deep-rgb), 0.18)
  );
  border: 1.5px solid rgba(var(--accent-rgb), 0.55);
  box-shadow: 0 0 26px rgba(var(--accent-rgb), 0.35);
}
.statusIcon svg {
  /* The icon fills most of its circle (Gabriel 2026-10-23). */
  width: 26px;
  height: 26px;
  stroke-width: 2.7;
}
.statusIconMuted {
  color: var(--text-secondary);
  background: var(--glass-bg);
  border-color: var(--border-soft);
  box-shadow: none;
}
.statusTitle {
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 23px;
  color: var(--text-primary);
  line-height: 1.05;
  margin: 0;
}
.statusSubtitle {
  font-size: 12px;
  color: var(--text-secondary);
  margin: 3px 0 0;
  line-height: 1.4;
}

/* ── Review header — REVIEW_* (no icon; title + subtitle) ── */
.reviewHead {
  padding-bottom: 16px;
  margin-bottom: 4px;
  border-bottom: 1px solid var(--border-subtle);
}
.reviewTitle {
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 22px;
  color: var(--text-primary);
  margin: 0;
}
.reviewSubtitle {
  font-size: 12.5px;
  color: var(--text-secondary);
  margin: 4px 0 0;
  line-height: 1.4;
}

/* ── Reserved-slot pill (kept in REVIEW views; mockup-silent, D2 parity) ── */
.reservedInfo {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 7px 11px;
  background: rgba(var(--accent-rgb), 0.1);
  border-radius: 10px;
  font-size: 12.5px;
  color: var(--accent);
}
.reservedInfo svg {
  width: 14px;
  height: 14px;
}

/* ── Detail rows (When · Duration · Location · Payment) ── */
.det {
  display: flex;
  flex-direction: column;
  padding-top: 4px;
}
.row {
  display: grid;
  grid-template-columns: 20px 1fr auto;
  gap: 14px;
  align-items: center;
  padding: 14px 0;
}
.row + .row {
  border-top: 1px solid var(--border-subtle);
}
.row > svg {
  width: 18px;
  height: 18px;
  color: var(--accent);
  stroke-width: 1.85;
}
.rowLabel {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-tertiary);
}
.rowValue {
  font-size: 14.5px;
  color: var(--text-primary);
  font-weight: 500;
  margin-top: 2px;
}
.rowValue + .rowValue {
  margin-top: 4px;
}
.rowAmount {
  font-size: 15px;
  font-weight: 700;
  color: var(--accent);
}
/* A row's own action on its right (Gabriel 2026-10-22): Add to Calendar beside When,
   Get directions beside the place; underlined text with its icon, as the website's
   Get directions. A link on the website, a button in the app. */
.rowLink {
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  background: none;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
  text-decoration: underline;
  text-decoration-color: var(--text-muted);
  text-underline-offset: 3px;
  white-space: nowrap;
  cursor: pointer;
}
.rowLink svg {
  flex-shrink: 0;
  width: 15px;
  height: 15px;
  color: var(--text-tertiary);
  stroke-width: 1.85;
}
.detDim .row > svg {
  color: var(--text-muted);
}
.detDim .rowValue {
  color: var(--text-secondary);
}

/* ── The call line's number field (service-by-phone D5; the drawing's open and no-number rows): the
   client's own Phone field as Profile edits it (ProfilePage .editInput) and ClientSheet's Save and Cancel. ── */
.phoneGrow {
  min-width: 0;
}
.phoneGrow > * + .phoneField {
  margin-top: 12px;
}
.phoneField {
  display: block;
}
.phoneFieldActive .phoneFieldLabel {
  color: var(--accent);
}
.phoneFieldLabel {
  display: block;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-tertiary);
  margin-bottom: 5px;
}
.phoneFieldBox {
  width: 100%;
  padding: 11px 13px;
  border-radius: 14px;
  background: var(--bg-inset);
  border: 1px solid var(--border-soft);
  font-size: 16px; /* >=16px so iOS never zooms on focus */
  color: var(--text-primary);
  font-family: inherit;
}
.phoneFieldBox:focus,
.phoneFieldActive .phoneFieldBox {
  outline: none;
  border-color: rgba(var(--accent-rgb), 0.5);
  box-shadow: 0 0 0 3px rgba(var(--accent-rgb), 0.12);
}
.phoneHint {
  display: block;
  margin-top: 8px;
}
.phoneActions {
  display: flex;
  gap: 10px;
  margin-top: 12px;
}
.phoneSave {
  flex: 1;
  padding: 13px;
  border-radius: 14px;
  background: var(--accent-orb);
  color: var(--text-on-accent);
  font-size: 14.5px;
  font-weight: 700;
  text-align: center;
  border: none;
  cursor: pointer;
  box-shadow: var(--glow-amber);
}
.phoneSave:disabled {
  opacity: 0.5;
  box-shadow: none;
}
.phoneCancel {
  padding: 13px 18px;
  border-radius: 14px;
  border: 1px solid var(--border-soft);
  background: transparent;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

/* ── Notice (REVIEW_APPROVAL / REVIEW_PAYMENT) ── */
.notice {
  display: flex;
  gap: 11px;
  align-items: flex-start;
  margin-top: 16px;
  padding: 12px 13px;
  border-radius: 14px;
  background: rgba(var(--accent-rgb), 0.08);
  border: 1px solid rgba(var(--accent-rgb), 0.22);
}
.notice svg {
  width: 17px;
  height: 17px;
  color: var(--accent-hot);
  flex-shrink: 0;
  margin-top: 1px;
  stroke-width: 2;
}
.noticeText {
  font-size: 12.5px;
  color: var(--text-secondary);
  line-height: 1.45;
}

/* ── Floating footer CTA bar + gradient scrim (2.6 rail) ── */
.foot {
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(
    var(--app-viewport-stable-height, 0px) - var(--app-viewport-height, var(--app-viewport-stable-height, 0px))
  );
  transition: bottom 250ms cubic-bezier(0.32, 0.72, 0, 1);
  z-index: 70;
  padding: 20px 16px calc(env(safe-area-inset-bottom, 0px) + 14px);
  background: linear-gradient(180deg, rgba(var(--bg-page-rgb), 0) 0%, rgba(var(--bg-page-rgb), 0.88) 32%, var(--bg-page) 100%);
}
.actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.btnPrimary {
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px;
  border-radius: 14px;
  background: var(--accent-orb);
  color: var(--text-on-accent);
  font-family: var(--font-body);
  font-size: 15.5px;
  font-weight: 700;
  letter-spacing: -0.01em;
  box-shadow: var(--glow-amber, 0 0 32px rgba(var(--accent-rgb), 0.28));
}
.btnPrimary:disabled {
  opacity: 0.6;
}
.btnPrimary svg {
  width: 17px;
  height: 17px;
  stroke-width: 2.6;
}
.btnSecondary {
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 13px;
  border-radius: 14px;
  background: var(--glass-bg);
  border: 1px solid var(--border-soft);
  color: var(--text-primary);
  font-family: var(--font-body);
  font-size: 14.5px;
  font-weight: 600;
}
.btnSecondary:disabled {
  opacity: 0.6;
}
.btnSecondary svg {
  width: 17px;
  height: 17px;
  color: var(--accent);
  stroke-width: 2;
}
.btnGhost {
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  background: none;
  width: 100%;
  padding: 11px;
  border-radius: 14px;
  color: var(--text-tertiary);
  font-family: var(--font-body);
  font-size: 13.5px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
}
.btnGhost:disabled {
  cursor: default;
}
.btnGhostDanger {
  color: var(--danger);
}
.btnSpinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: var(--text-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* Match booking detail's mutation feedback: once cancellation starts, cover
   the sheet and page until the live CANCELLED flow state has been refetched. */
.cancelFeedbackBackdrop {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: grid;
  place-items: center;
  padding: 28px;
  background: rgba(var(--bg-page-rgb), 0.72);
  backdrop-filter: blur(8px) saturate(115%);
  animation: cancelBackdropIn 0.18s ease-out both;
}
.cancelProgress {
  min-width: 142px;
  padding: 20px 24px;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 11px;
  background: var(--glass-bg-strong);
  backdrop-filter: var(--glass-blur);
  box-shadow: var(--glass-shadow);
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 650;
}
.cancelSpinner {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 3px solid var(--border-subtle);
  border-top-color: var(--accent);
  animation: spin 0.72s linear infinite;
}
@keyframes cancelBackdropIn {
  from { opacity: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .cancelFeedbackBackdrop,
  .cancelSpinner {
    animation-duration: 0.01ms;
  }
}

/* ── Expired / failed centered error state (mockup State 6 — no hero) ── */
.errLayout {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  z-index: 2;
}
.errWrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 30px;
}
.warn {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: rgba(var(--fg-page-rgb), 0.05);
  border: 1px solid var(--border-soft);
  color: var(--accent-hot);
  margin-bottom: 18px;
}
.warn svg {
  width: 32px;
  height: 32px;
  stroke-width: 1.8;
}
.errTitle {
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 27px;
  color: var(--text-primary);
  margin: 0;
}
.errMsg {
  margin-top: 10px;
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.55;
  max-width: 280px;
}
.errFoot {
  padding: 18px 24px calc(env(safe-area-inset-bottom, 0px) + 16px);
}

/* ── Loading + session/flow error states (themed; no ProgressBar) ── */
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  min-height: 100dvh;
  gap: 16px;
  color: var(--text-secondary);
}
.loading p {
  margin: 0;
}
.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--glass-bg-strong);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 28px;
  text-align: center;
}
.error h2 {
  font-family: var(--font-display);
  font-size: 24px;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}
.error p {
  color: var(--text-secondary);
  margin: 0 0 24px 0;
  max-width: 280px;
}
.error button {
  padding: 12px 24px;
  background: var(--accent);
  color: var(--text-on-accent);
  border: none;
  border-radius: 14px;
  font-family: var(--font-body);
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
}
